'use client';
import React, { useState } from 'react';
import { Lightbulb, BarChart2, Zap, ChevronDown } from 'lucide-react';

interface WalkthroughQuestion {
  id: string;
  difficulty: string;
  difficultyIndex: number;
  keywordEffectiveness: string;
  mainIdeaPattern: string;
  synonymMap: Array<{ id: string; passageWord: string; questionWord: string }>;
  strategyTip: { title: string; steps: Array<{ id: string; text: string }> };
}

function AccordionSection({
  title,
  icon,
  defaultOpen = false,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3.5 py-3 bg-muted/40 hover:bg-muted/70 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          {icon}
          <p className="text-xs font-semibold text-foreground">{title}</p>
        </div>
        <ChevronDown
          size={14}
          className={`text-muted-foreground flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && <div className="px-3.5 pb-3.5 pt-2.5 bg-card space-y-2.5">{children}</div>}
    </div>
  );
}

export default function WalkthroughSidebar({ question }: { question: WalkthroughQuestion }) {
  const diffColor =
    question.difficultyIndex >= 70 ? 'text-accent' :
    question.difficultyIndex >= 50 ? 'text-info' :
    question.difficultyIndex >= 40 ? 'text-warning' : 'text-danger';

  const diffBarColor =
    question.difficultyIndex >= 70 ? 'bg-accent' :
    question.difficultyIndex >= 50 ? 'bg-info' :
    question.difficultyIndex >= 40 ? 'bg-warning' : 'bg-danger';

  return (
    <div className="flex flex-col min-h-0 h-full overflow-y-auto scrollbar-thin border-l border-border bg-muted/20">
      {/* Header — only shown on desktop */}
      <div className="flex-shrink-0 px-3.5 py-2.5 bg-muted/50 border-b border-border hidden lg:block">
        <p className="text-xs font-semibold text-foreground">Learning Insights</p>
        <p className="text-xs text-muted-foreground mt-0.5">Metadata &amp; strategy for this question</p>
      </div>

      <div className="flex-1 p-3 sm:p-4 space-y-3">

        {/* Question Metadata */}
        <AccordionSection
          title="Question Metadata"
          icon={<BarChart2 size={13} className="text-primary" />}
          defaultOpen={true}
        >
          {/* Difficulty */}
          <div>
            <p className="text-xs text-muted-foreground mb-1">Difficulty</p>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-bold ${diffColor}`}>{question.difficulty}</span>
              <div className="flex-1 h-1.5 bg-border rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${diffBarColor}`}
                  style={{ width: `${question.difficultyIndex}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground tabular-nums">{question.difficultyIndex}%</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{question.difficultyIndex}% of candidates answer correctly</p>
          </div>

          {/* Keyword effectiveness */}
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Keyword Effectiveness</p>
            <p className="text-xs font-semibold text-warning">{question.keywordEffectiveness}</p>
            <p className="text-xs text-muted-foreground leading-snug">Paraphrase recognition required — keywords alone won't locate the answer</p>
          </div>

          {/* Main idea pattern */}
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Main Idea Pattern</p>
            <p className="text-xs font-medium text-foreground leading-relaxed">{question.mainIdeaPattern}</p>
          </div>
        </AccordionSection>

        {/* Synonym Map */}
        <AccordionSection
          title="Synonym Map"
          icon={<BarChart2 size={13} className="text-primary" />}
          defaultOpen={true}
        >
          {question.synonymMap.map((s) => (
            <div key={s.id} className="flex items-center gap-1.5 flex-wrap text-xs">
              <span className="paraphrase-pill-passage">{s.passageWord}</span>
              <span className="text-muted-foreground">↔</span>
              <span className="paraphrase-pill-question">{s.questionWord}</span>
            </div>
          ))}
        </AccordionSection>

        {/* Strategy Tip */}
        <AccordionSection
          title="Strategy Tip"
          icon={<Lightbulb size={13} className="text-warning" />}
          defaultOpen={false}
        >
          <p className="text-xs font-semibold text-foreground">{question.strategyTip.title}</p>
          <div className="space-y-2 mt-1">
            {question.strategyTip.steps.map((step, i) => (
              <div key={step.id} className="flex items-start gap-2">
                <span className="w-4 h-4 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <p className="text-xs text-foreground leading-relaxed">{step.text}</p>
              </div>
            ))}
          </div>
        </AccordionSection>

        {/* Quick Reference */}
        <AccordionSection
          title="Quick Reference"
          icon={<Zap size={13} className="text-accent" />}
          defaultOpen={false}
        >
          <div className="space-y-1.5 text-xs">
            {[
              { id: 'qr-true', label: 'True', desc: 'Passage directly confirms the statement' },
              { id: 'qr-false', label: 'False', desc: 'Passage directly contradicts the statement' },
              { id: 'qr-ng', label: 'Not Given', desc: 'Passage does not address the statement at all' },
            ].map((ref) => (
              <div key={ref.id} className="flex items-start gap-2">
                <span className="font-bold text-primary w-16 flex-shrink-0">{ref.label}:</span>
                <span className="text-muted-foreground leading-relaxed">{ref.desc}</span>
              </div>
            ))}
          </div>
        </AccordionSection>
      </div>
    </div>
  );
}